import React from 'react';

import { Switch } from 'antd';

import { isJobEvent } from '@libs/EventTypes';
import { isSameDate, showDateNoTime, showPeriodNoTime, showTimePeriod } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import SelectionBar from '@components/Gui/SelectionBar';
import ProjectEventSummary from '@components/Project/ProjectEventSummary';

import { useSendProjectDrawer } from './SendProjectDrawerContext';
import { EventContent } from './SendProjectDrawerUI';

/**
 * EventBlock displays a single event of the visible list and holds the selection
 * controls: every model of the event, every model of a work date, one model.
 */
const EventBlock = ({ event }) => {
    const { state, actions } = useSendProjectDrawer();
    const { selectedEvents } = state;

    const isBooking = event.elType === 'Event';
    const selectionKey = isBooking ? 'EventDatesModels' : `${event.elType}s`;

    /**
     * Checks if a model is switched on
     */
    const isSelected = (id) => Boolean(selectedEvents && selectedEvents[selectionKey].includes(id));

    /**
     * Selection shortcuts, same shape at every level.
     * "active" keeps the confirmed models: no cancelled and, on job events, no options.
     */
    const selectionActions = (onSelect) => [
        { label: 'all', method: () => onSelect('all') },
        { label: 'active', method: () => onSelect('active') },
        { label: 'none', method: () => onSelect('none') },
    ];

    const renderModel = ({ id, modelID, className = '', suffix = '' }) => (
        <div className={`event-date-model ${className}`.trim()} key={id}>
            <div className="switch-container">
                <Switch
                    checked={isSelected(id)}
                    onChange={(checked) => actions.selectModel(selectionKey, id, checked)}
                />
            </div>
            <div className="event-date-model-name">
                {getContactName(modelID).toLowerCase()}
                {suffix}
            </div>
        </div>
    );

    const renderEventSummary = () => (
        <div className="event-summary">
            <ProjectEventSummary event={event} type={isBooking ? undefined : event.Type || event.TypeName} />
            <SelectionBar
                className="selection-actions"
                actions={selectionActions((mode) => actions.selectEventModels(event, mode))}
            />
        </div>
    );

    if (isBooking) {
        return (
            <EventContent>
                {renderEventSummary()}
                <div className="event-dates">
                    {event.EventDates.map((eventDate) => (
                        <div className="event-date" key={eventDate.ID}>
                            <div className="event-date-when">
                                <p className="event-date-day">{showDateNoTime(eventDate.FromDate)}</p>
                                <p className="event-date-time">
                                    {showTimePeriod(eventDate.FromDate, eventDate.ToDate)}
                                </p>
                            </div>
                            <div className="event-date-models">
                                {eventDate.EventDatesModels.map((eventDateModel) => {
                                    const isOption = eventDateModel.OptionNumber < 10 && isJobEvent(event.TypeName);
                                    return renderModel({
                                        id: eventDateModel.ID,
                                        modelID: eventDateModel.ModelID,
                                        className: `${eventDateModel.Cancel ? 'cancelled' : ''} ${
                                            isOption ? 'option' : ''
                                        }`.trim(),
                                        suffix: isOption ? ` [option: ${eventDateModel.OptionNumber}]` : '',
                                    });
                                })}
                            </div>
                            {eventDate.EventDatesModels.length > 1 && (
                                <SelectionBar
                                    className="selection-actions"
                                    actions={selectionActions((mode) =>
                                        actions.selectDateModels(event, eventDate, mode)
                                    )}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </EventContent>
        );
    }

    return (
        <EventContent>
            {renderEventSummary()}
            <div className="event-dates">
                <div className="event-date">
                    <div className="event-date-when">
                        <p className="event-date-day">{showPeriodNoTime(event.CommonStartDate, event.CommonEndDate)}</p>
                        <p className="event-date-time">
                            {isSameDate(event.CommonStartDate, event.CommonEndDate)
                                ? showTimePeriod(event.CommonStartDate, event.CommonEndDate)
                                : 'whole period'}
                        </p>
                    </div>
                    <div className="event-date-models">
                        {event.Models.map((model) =>
                            renderModel({
                                id: model.CommonID,
                                modelID: model.ModelID,
                                className: model.Cancel ? 'cancelled' : '',
                            })
                        )}
                    </div>
                </div>
            </div>
        </EventContent>
    );
};

export default EventBlock;
